import type { EstimateRequest, EstimateResult, PaymentSnapshot } from "../../../lib/agent-types";
import type { FallbackPaymentPreview } from "../../../lib/financials";
import {
  formatCurrencyFromCents,
  formatPercent,
  getEffectiveRatePercent,
  getPaymentBreakdown,
  getPaymentSnapshot,
  getPaymentSummary,
} from "../../../lib/financials";
import { cn } from "../../../lib/utils";

const BREAKDOWN_TONES = [
  "bg-primary",
  "bg-primary/70",
  "bg-secondary-foreground",
  "bg-accent-foreground",
  "bg-muted-foreground/45",
] as const;

interface EstimateLike {
  results: EstimateResult[];
}

type PreviewModel = {
  downPaymentPercent: number | null;
  loanTermMonths: number;
  payment: PaymentSnapshot;
  sourceLabel: string;
  summary: string;
  effectiveRatePercent: number | null;
};

export function MonthlyPaymentPreview({
  estimate,
  fallback = null,
  isLoading = false,
  request = null,
}: {
  estimate: EstimateLike | null;
  fallback?: FallbackPaymentPreview | null;
  isLoading?: boolean;
  request?: EstimateRequest | null;
}) {
  const result = estimate?.results[0] ?? null;
  const model = buildPreviewModel(result, request, fallback);

  if (!model) {
    return (
      <div className="rounded-[1.25rem] border border-dashed border-border/80 bg-background/70 px-4 py-4 text-sm text-muted-foreground">
        {isLoading
          ? "Refreshing your scenario…"
          : "Move a control to see the monthly payment mix."}
      </div>
    );
  }

  return <PreviewContent model={model} />;
}

function PreviewContent({
  model,
}: {
  model: PreviewModel;
}) {
  const breakdown = getPaymentBreakdown(model.payment);

  return (
    <section className="space-y-4">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="eyebrow-label">{model.sourceLabel}</p>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Output
          </p>
        </div>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Monthly payment
          </p>
          <p
            className="display-figure mt-1.5 text-[2.5rem] leading-[1.05] tracking-[-0.05em] text-foreground sm:text-[3rem]"
            data-slot="monthly-payment-headline"
          >
            {formatCurrencyFromCents(model.payment.total)}
          </p>
        </div>

        <p className="max-w-lg text-sm leading-6 text-muted-foreground">
          {model.summary}
        </p>
      </div>

      <div className="overflow-hidden rounded-[1.25rem] bg-background/80">
        <div className="grid grid-cols-2">
          <MetricCell
            className="border-b border-r border-border/70"
            label="Rate"
            value={formatPercent(model.effectiveRatePercent)}
          />
          <MetricCell
            className="border-b border-border/70"
            label="Down"
            value={
              model.downPaymentPercent === null
                ? "—"
                : formatPercent(model.downPaymentPercent, 1)
            }
          />
          <MetricCell
            className="border-r border-border/70"
            label="Loan"
            value={formatCurrencyFromCents(model.payment.loan_amount)}
          />
          <MetricCell
            label="Term"
            value={`${Math.round(model.loanTermMonths / 12)} years`}
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-[1.25rem] bg-background/80">
        <div className="flex items-center justify-between gap-3 border-b border-border/70 px-4 py-3">
          <p className="eyebrow-label">Breakdown</p>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            PITI
          </p>
        </div>

        <div className="divide-y divide-border/50">
          {breakdown.map((item, index) => (
            <div
              key={item.key}
              className="flex items-center justify-between gap-4 px-4 py-2.5"
            >
              <span className="inline-flex items-center gap-2 text-[13px] text-muted-foreground">
                <span
                  aria-hidden
                  className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    BREAKDOWN_TONES[index % BREAKDOWN_TONES.length],
                  )}
                />
                {item.label}
              </span>
              <span className="text-[13px] font-medium tabular-nums text-foreground/80">
                {formatCurrencyFromCents(item.value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MetricCell({
  className,
  label,
  value,
}: {
  className?: string;
  label: string;
  value: string;
}) {
  return (
    <div className={cn("px-4 py-3", className)}>
      <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1.5 text-sm font-bold tabular-nums text-foreground">{value}</p>
    </div>
  );
}

function buildPreviewModel(
  result: EstimateResult | null,
  request: EstimateRequest | null,
  fallback: FallbackPaymentPreview | null,
): PreviewModel | null {
  if (result) {
    const payment = getPaymentSnapshot(result);

    if (!payment) {
      return null;
    }

    return {
      downPaymentPercent: derivedDownPaymentPercent(request, payment),
      effectiveRatePercent: getEffectiveRatePercent(result),
      loanTermMonths: request?.financing.loan_term_months ?? 360,
      payment,
      sourceLabel: "Live estimate",
      summary: getPaymentSummary(result),
    };
  }

  if (!fallback) {
    return null;
  }

  return {
    downPaymentPercent: fallback.downPaymentPercent,
    effectiveRatePercent: fallback.effectiveRatePercent,
    loanTermMonths: fallback.loanTermMonths,
    payment: fallback.payment,
    sourceLabel: fallback.sourceLabel,
    summary: fallback.summary,
  };
}

// derivedDownPaymentPercent prefers an explicit down_payment_percent on the
// request and falls back to computing it from target_price − loan_amount.
// The advisor's calculator tool submits only DownPaymentAmount today, so
// without this fallback the "Down" metric cell renders as an em dash.
function derivedDownPaymentPercent(
  request: EstimateRequest | null,
  payment: PaymentSnapshot,
): number | null {
  if (typeof request?.financing.down_payment_percent === "number") {
    return request.financing.down_payment_percent * 100;
  }
  const target = request?.property.target_price;
  if (typeof target !== "number" || target <= 0) {
    return null;
  }
  const loanAmount = payment.loan_amount;
  if (typeof loanAmount !== "number" || loanAmount < 0) {
    return null;
  }
  const downCents = target - loanAmount;
  if (downCents < 0) {
    return null;
  }
  return (downCents / target) * 100;
}
